import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { HttpHelper } from '@umss/shared/utils';
import { Observable } from 'rxjs';
import {
  CorporatePayPeriodStatsDto,
  CorporationDto,
  CorporationSearchDto,
  CreateCorporationDto,
  PagedResultDto,
  RegisterCorporateFacilityRequestDto,
  RegisterCorporateFacilityResponseDto,
  UpdateCorporationDto,
  CreateFacilityCorporateLinkRequestDto,
  FacilityCorporateLinkRequestDto
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class CorporateService {
  private readonly baseUrl = '/api/Corporate';
  private readonly http = inject(HttpClient);

  getAll(
    parameters?: CorporationSearchDto
  ): Observable<PagedResultDto<CorporationDto>> {
    const params = HttpHelper.toHttpParams(parameters ?? {});
    return this.http.get<PagedResultDto<CorporationDto>>(`${this.baseUrl}`, {
      params
    });
  }

  create(requestBody: CreateCorporationDto): Observable<CorporationDto> {
    return this.http.post<CorporationDto>(`${this.baseUrl}`, requestBody);
  }

  get(id: string): Observable<CorporationDto> {
    return this.http.get<CorporationDto>(`${this.baseUrl}/${id}`);
  }

  update(
    id: string,
    requestBody: UpdateCorporationDto
  ): Observable<CorporationDto> {
    return this.http.put<CorporationDto>(`${this.baseUrl}/${id}`, requestBody);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  getPayPeriodStats(
    corporateId: string,
    periodStartDate: string
  ): Observable<CorporatePayPeriodStatsDto> {
    const params = HttpHelper.toHttpParams({ periodStartDate });
    return this.http.get<CorporatePayPeriodStatsDto>(
      `${this.baseUrl}/${corporateId}/pay-period-stats`,
      { params }
    );
  }

  registerFacility(
    corporateId: string,
    body: RegisterCorporateFacilityRequestDto
  ): Observable<RegisterCorporateFacilityResponseDto> {
    return this.http.post<RegisterCorporateFacilityResponseDto>(
      `${this.baseUrl}/${corporateId}/facilities/register`,
      body
    );
  }

  // ============================================
  // FACILITY-CORPORATE LINK REQUEST ENDPOINTS
  // ============================================

  /**
   * Create a new link request between facility and corporate
   * This sends a request to link a facility to a corporate
   */
  createFacilityCorporateLinkRequest(
    body: CreateFacilityCorporateLinkRequestDto
  ): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.post<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/link-requests`,
      body
    );
  }

  /**
   * Get link request by ID
   */
  getFacilityCorporateLinkRequest(
    requestId: string
  ): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.get<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/link-requests/${requestId}`
    );
  }

  /**
   * Approve a facility-corporate link request (as business admin)
   */
  approveFacilityCorporateLinkRequest(
    requestId: string
  ): Observable<FacilityCorporateLinkRequestDto> {
    return this.http.post<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/link-requests/${requestId}/approve`,
      {}
    );
  }

  /**
   * Reject a facility-corporate link request
   */
  rejectFacilityCorporateLinkRequest(
    requestId: string,
    rejectionReason?: string
  ): Observable<FacilityCorporateLinkRequestDto> {
    const body = rejectionReason ? { rejectionReason } : {};
    return this.http.post<FacilityCorporateLinkRequestDto>(
      `${this.baseUrl}/link-requests/${requestId}/reject`,
      body
    );
  }
}
